import React, { useMemo, useState } from "react"
import { Button, Tooltip } from "antd"
import pencilIcon from "assets/svg/pencil.svg"
import trashIcon from "assets/svg/trash.svg"
import chevronUpIcon from "assets/svg/chevron-up.svg"
import chevronDownIcon from "assets/svg/chevron-down.svg"
import {
  getDaysInDateRange,
  WEEK_DAYS as DAYS,
  type DayOfWeek,
} from "../../utils/shiftTablesUtils"
import DateInput, { DateInputValue } from "./DateInput"

// ── types ────────────────────────────────────────────────────────────

export interface StaffCell {
  staffCount: number | null // null = empty (show + button)
}

export interface RosterShiftPeriod {
  id: string
  name: string
  startTime: string
  endTime: string
  schedule: Record<DayOfWeek, StaffCell>
}

export interface RosterTable {
  id: string
  dateLabel: string
  endDateLabel: string
  startDate: DateInputValue
  endDate: DateInputValue
  shiftPeriods: RosterShiftPeriod[]
  collapsed: boolean
}

interface RosterTableCardProps {
  table: RosterTable
  onToggleCollapse: () => void
  onDelete: () => void
  onUpdateStartDate: (val: DateInputValue) => void
  onUpdateEndDate: (val: DateInputValue) => void
  onAddStaff: (periodId: string, day: DayOfWeek) => void
  onSaveDuration?: () => boolean
}

const GRID_COLS = "grid grid-cols-[140px_repeat(7,1fr)]"

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
]

const formatDate = (d?: DateInputValue): string => {
  if (!d?.dd || !d?.mm || !d?.yyyy) return "—"
  const month = MONTHS[Number(d.mm) - 1] ?? d.mm
  return `${d.dd} ${month} ${d.yyyy}`
}

const endSlotIcon =
  "https://padehrs3-staging.s3.us-east-2.amazonaws.com/images/49ee8442-4666-4558-bdc4-977b22b24c64.svg"

// ── component ────────────────────────────────────────────────────────
const RosterTableCard: React.FC<RosterTableCardProps> = ({
  table,
  onToggleCollapse,
  onDelete,
  onUpdateStartDate,
  onUpdateEndDate,
  onAddStaff,
  onSaveDuration,
}) => {
  const [editingDuration, setEditingDuration] = useState(false)

  // Only the weekdays the shift table's duration covers accept assignments.
  const activeDays = useMemo(
    () => getDaysInDateRange(table.startDate, table.endDate),
    [table.startDate, table.endDate]
  )

  const copyId = (id: string) => {
    navigator.clipboard?.writeText(id)
  }

  return (
    <div className="flex flex-col gap-3">
      {/* ── Duration row + delete ───────────────────────────────────── */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm text-[#64748B]">Shift table duration:</span>
          {editingDuration ? (
            <div className="flex items-center gap-2">
              <DateInput
                value={table.startDate}
                onChange={onUpdateStartDate}
              />
              <span className="text-[#94A3B8] text-sm">to</span>
              <DateInput
                value={table.endDate}
                onChange={onUpdateEndDate}
              />
              <button
                type="button"
                className="text-sm font-semibold text-[#004AF5]"
                onClick={() => {
                  if (onSaveDuration?.() !== false) setEditingDuration(false)
                }}
              >
                Done
              </button>
            </div>
          ) : (
            <span className="flex items-center gap-1.5 text-sm font-bold text-[#0F172B]">
              {formatDate(table.startDate)} to {formatDate(table.endDate)}
              <button
                type="button"
                onClick={() => setEditingDuration(true)}
                className="text-[#64748B] hover:text-[#0F172B]"
                aria-label="Edit shift table duration"
              >
                <img
                  src={pencilIcon}
                  width={14}
                  height={14}
                  alt=""
                />
              </button>
            </span>
          )}
        </div>

        <Button
          type="default"
          className="self-start font-semibold px-4 h-9 rounded-lg border-2 border-[#E2E8F0] text-[#0F172B] text-sm flex items-center gap-2"
          onClick={onDelete}
        >
          <img
            src={trashIcon}
            width={14}
            height={14}
            alt=""
          />
          Delete this shift table
        </Button>
      </div>

      {/* ── Schedule grid ───────────────────────────────────────────── */}
      <div className="border border-[#E2E8F0] rounded-lg overflow-hidden">
        {/* header row: collapse toggle + days of week */}
        <div
          className={`${GRID_COLS} bg-[#F8FAFC] ${
            !table.collapsed ? "border-b border-[#E2E8F0]" : ""
          }`}
        >
          <Tooltip title={table.collapsed ? "Expand table" : "Collapse table"}>
            <button
              type="button"
              className="flex items-center justify-center border-r border-[#E2E8F0] hover:bg-[#F1F5F9]"
              onClick={onToggleCollapse}
            >
              <img
                src={table.collapsed ? chevronDownIcon : chevronUpIcon}
                width={16}
                height={16}
                alt=""
              />
            </button>
          </Tooltip>
          {DAYS.map((day) => (
            <Tooltip
              title={
                activeDays.has(day)
                  ? undefined
                  : `${day} is outside this shift table's duration`
              }
              key={day}
            >
              <span
                className={`px-2 py-3 text-center font-semibold text-sm ${
                  activeDays.has(day) ? "text-[#62748E]" : "text-[#CAD5E2]"
                }`}
              >
                {day}
              </span>
            </Tooltip>
          ))}
        </div>

        {/* shift period rows (hidden when collapsed) */}
        {!table.collapsed &&
          table.shiftPeriods.map((period, periodIndex) => (
            <div
              key={period.id}
              className={`${GRID_COLS} ${
                periodIndex < table.shiftPeriods.length - 1
                  ? "border-b border-[#E2E8F0]"
                  : ""
              }`}
            >
              {/* shift period info */}
              <div className="px-3 py-4 flex flex-col gap-1.5 border-r border-[#E2E8F0]">
                <span className="font-bold text-sm text-[#0F172B]">
                  {period.name}
                </span>
                <div className="border-l-2 border-[#CAD5E2] pl-2 flex flex-col">
                  <span className="text-xs text-[#64748B]">
                    {period.startTime}
                  </span>
                  <span className="text-xs text-[#64748B]">
                    {period.endTime}
                  </span>
                </div>
                <span className="flex items-center gap-1 text-xs text-[#0F172B]">
                  ID: <span className="font-semibold">{period.id}</span>
                  <button
                    type="button"
                    onClick={() => copyId(period.id)}
                    className="text-[#64748B] hover:text-[#0F172B]"
                    aria-label="Copy shift period ID"
                  >
                    <img
                      src={endSlotIcon}
                      width={12}
                      height={12}
                      alt=""
                    />
                  </button>
                </span>
              </div>

              {/* day cells */}
              {DAYS.map((day) => {
                const cell = period.schedule[day]
                const hasStaff = cell.staffCount !== null
                const isActive = activeDays.has(day)
                const outOfRangeTitle = `${day} is outside this shift table's duration`
                return (
                  <div
                    key={day}
                    className={`flex items-center justify-center px-3 py-4 ${
                      isActive ? "" : "bg-[#F8FAFC]"
                    }`}
                  >
                    {/* The span is the hover target. A disabled button gets no
                        pointer events, and the disabled tooltip is the one that
                        says why the day cannot be staffed. */}
                    {hasStaff ? (
                      <Tooltip
                        title={
                          isActive ? "View/add/remove staff" : outOfRangeTitle
                        }
                      >
                        <span
                          className={`inline-flex ${
                            isActive ? "" : "cursor-not-allowed"
                          }`}
                        >
                          <button
                            type="button"
                            disabled={!isActive}
                            className={`flex flex-col items-start ${
                              isActive
                                ? ""
                                : "opacity-40 cursor-not-allowed pointer-events-none"
                            }`}
                            onClick={() => onAddStaff(period.id, day)}
                          >
                            <span className="text-xs font-semibold text-[#0F172B]">
                              {cell.staffCount} staff
                            </span>
                            <span className="flex items-center gap-1 text-xs text-[#64748B]">
                              Added
                              <img
                                src={pencilIcon}
                                width={12}
                                height={12}
                                alt=""
                                className="opacity-50"
                              />
                            </span>
                          </button>
                        </span>
                      </Tooltip>
                    ) : (
                      <Tooltip title={isActive ? "Add staff" : outOfRangeTitle}>
                        <span
                          className={`inline-flex ${
                            isActive ? "" : "cursor-not-allowed"
                          }`}
                        >
                          <button
                            type="button"
                            disabled={!isActive}
                            className={`w-8 h-8 flex items-center justify-center border-2 rounded-lg text-lg ${
                              isActive
                                ? "border-[#E2E8F0] text-[#64748B] hover:bg-[#F1F5F9] hover:text-[#0F172B]"
                                : "border-[#F1F5F9] text-[#CAD5E2] cursor-not-allowed pointer-events-none"
                            }`}
                            onClick={() => onAddStaff(period.id, day)}
                          >
                            +
                          </button>
                        </span>
                      </Tooltip>
                    )}
                  </div>
                )
              })}
            </div>
          ))}
      </div>
    </div>
  )
}

export default RosterTableCard
